import { useEffect, useRef, useState } from "react";
import { Search, X, ArrowUpRight, Play } from "lucide-react";
import { api } from "@/lib/api";

export const SearchModal = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [posts, setPosts] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/posts").then(({ data }) => setPosts(data));
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const results = q
    ? posts.filter((p) =>
        [p.title, p.summary, p.source, p.category, p.content_type]
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
    : [];

  return (
    <div data-testid="search-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 pt-20" onClick={onClose}>
      <div className="bg-surface w-full max-w-2xl border border-rule" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-rule px-5 py-4">
          <Search size={20} className="text-signal" />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the archive — stories, sources, topics…"
            className="flex-1 bg-transparent font-body text-lg text-ink placeholder:text-muted-ink focus:outline-none"
          />
          <button data-testid="search-close" onClick={onClose} className="text-muted-ink hover:text-signal">
            <X size={20} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {q && results.length === 0 && (
            <p className="font-mono2 text-xs text-muted-ink uppercase tracking-[0.2em] p-8 text-center">// No files match "{query}"</p>
          )}
          {!q && (
            <p className="font-mono2 text-xs text-muted-ink uppercase tracking-[0.2em] p-8 text-center">// Begin typing to decrypt the archive</p>
          )}
          {results.map((p) => (
            <a
              key={p.id}
              data-testid={`search-result-${p.id}`}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 px-5 py-4 border-b border-rule hover:bg-paper transition-colors group"
            >
              <img src={p.image_url} alt="" className="w-14 h-14 object-cover shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-mono2 text-[0.6rem] bg-ink text-paper px-1.5 py-0.5 uppercase">{p.source}</span>
                  <span className="overline-label text-muted-ink">{p.category}</span>
                </div>
                <h3 className="font-grotesk font-semibold text-sm text-ink truncate group-hover:text-signal transition-colors">{p.title}</h3>
              </div>
              {p.content_type === "video" ? <Play size={16} className="text-signal shrink-0" /> : <ArrowUpRight size={16} className="text-muted-ink group-hover:text-signal shrink-0" />}
            </a>
          ))}
        </div>

        {q && results.length > 0 && (
          <div className="px-5 py-3 border-t border-rule">
            <span className="font-mono2 text-[0.65rem] text-muted-ink uppercase tracking-[0.2em]">{results.length} file{results.length > 1 ? "s" : ""} found · opens at source</span>
          </div>
        )}
      </div>
    </div>
  );
};
